import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Award, 
  CheckCircle, 
  Users,
  TrendingUp,
  Globe,
  ArrowRight,
  Gift
} from 'lucide-react';
import { openWhatsAppQuote } from '../utils/whatsapp';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  const features = [
    {
      icon: <CheckCircle className="h-12 w-12 text-blue-500" />,
      title: "Premium Safety Nets",
      description: "High-quality safety nets with 5+ year warranty"
    },
    {
      icon: <Zap className="h-12 w-12 text-blue-500" />,
      title: "Expert Installation",
      description: "Certified technicians with 10+ years of experience"
    },
    {
      icon: <Award className="h-12 w-12 text-blue-500" />,
      title: "Quality Assurance",
      description: "ISO certified processes and materials"
    },
    {
      icon: <CheckCircle className="h-12 w-12 text-blue-500" />,
      title: "Best Service",
      description: "Award-winning customer service and support"
    }
  ];

  const stats = [
    { number: "500+", label: "Safety Net Installations", icon: <CheckCircle className="h-6 w-6" /> },
    { number: "30+", label: "Team Members", icon: <Users className="h-6 w-6" /> },
    { number: "15+", label: "Years Experience", icon: <TrendingUp className="h-6 w-6" /> },
    { number: "25+", label: "Cities Served", icon: <Globe className="h-6 w-6" /> }
  ];

  return (
    <div className="pt-16">
      <SEO 
        pageType="home"
        customTitle="JaraNets Safety Solutions - Professional Safety Nets & Invisible Grill Installation Services"
        customDescription="Leading provider of safety nets, invisible grill, and pigeon nets installation services across India. Expert installation, quality materials, and reliable service. Get free quote today."
        customKeywords="safety nets installation, invisible grill, pigeon nets, construction safety nets, industrial safety nets, child safety nets, car parking nets, swimming pool nets, cloth hangers, safety solutions India"
      />
      {/* Hero Section with Cricket Nets Video Background */}
      <section className="relative bg-gradient-to-br from-blue-900/60 via-blue-800/50 to-blue-900/60 text-white min-h-screen flex items-center">
        {/* Cricket Nets Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-50"
            onError={(e) => {
              console.log('Video failed to load, using fallback image');
              // Show fallback image when video fails
              const fallbackDiv = document.createElement('div');
              fallbackDiv.className = 'absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat';
              fallbackDiv.style.backgroundImage = `url('/images/building-nets.webp')`;
              if (e.currentTarget.parentNode) {
                e.currentTarget.parentNode.appendChild(fallbackDiv);
                e.currentTarget.style.display = 'none';
              }
            }}
            onLoadStart={() => {
              console.log('Attempting to load Cricket-Players-Practicing-In-The-Nets video...');
            }}
            onCanPlay={() => {
              console.log('Video loaded successfully and can play');
            }}
            onLoadedData={(e) => {
              console.log('Video data loaded');
              e.currentTarget.play().catch(err => console.log('Auto-play failed:', err));
            }}
          >
            <source src="/videos/Cricket-Players-Practicing-In-The-Nets.mp4" type="video/mp4" />
            <source src="/videos/Cricket-Players-Practicing-In-The-Nets.webm" type="video/webm" />
          </video>
        </div>
        
        
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div>
              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Secure Every Step — Trustworthy Safety Nets for Every Height
              </h1>
              
              {/* Subheading */}
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
                Professional Safety Net Installation & Protection Solutions
              </p>
              
              {/* Description */}
              <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                Based in Karmanghat, Hyderabad - Telangana, we are a leading safety nets installation company serving all areas of Hyderabad. Protect your workers, property, and assets with our comprehensive safety net solutions.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => openWhatsAppQuote()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                                 <Link
                   to="/landing"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center border-2 border-green-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                 >
                  <Gift className="mr-2 h-5 w-5" />
                  Get Safety Consultation
                 </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-300">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-sm font-medium">500+ Installations</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-sm font-medium">5+ Years Experience</span>
                </div>
                </div>
              </div>
          </div>
        </div>
      </section>

      {/* Our Safety Net Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Safety Net Products
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive safety net solutions for every need - serving Andhra Pradesh and Telangana with professional installation and quality materials
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

            {/* 1. Balcony Grills */}
            <Link to="/invisible-grill-balconies" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
              <img 
                src="/images/invisible-grill-balconies.jpg"
                  alt="Balcony Grills"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Balcony Grills</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Transparent safety grills specifically designed for balcony protection without compromising your view.</p>
              </div>
            </Link>

            {/* 2. Window Grills */}
            <Link to="/invisible-grill-windows" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
              <img 
                src="/images/invisible-grill-windows.jpg"
                  alt="Window Grills"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Window Grills</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Transparent safety grills for window protection and security without compromising natural light.</p>
              </div>
            </Link>

            {/* 3. Apartment Grills */}
            <Link to="/invisible-grill-apartments" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
              <img 
                src="/images/invisible-grill-apartments.jpg"
                  alt="Apartment Grills"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Apartment Grills</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Comprehensive transparent safety solutions for apartment buildings and communities.</p>
              </div>
            </Link>

            {/* 4. Anti Bird Net (Pigeon Nets) */}
            <Link to="/pigeon-nets" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
                <img 
                  src="/images/pigeon-nets-specific.webp"
                  alt="Anti Bird Net"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Anti Bird Net</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Anti-bird nets to protect buildings from pigeons and other birds.</p>
              </div>
            </Link>

            {/* 5. Cricket Practice Net (Sports Safety) */}
            <Link to="/sports-safety" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
                <img 
                src="/images/cricket-practice-nets-custom.jpg"
                  alt="Cricket Practice Net"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Cricket Practice Net</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Cricket nets, football nets, and tennis court protection for sports facilities.</p>
              </div>
            </Link>

            {/* 6. Children Safety Nets */}
            <Link to="/child-safety-net" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
              <img 
                src="/images/children-safety-nets-custom.jpg"
                  alt="Children Safety Nets"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Children Safety Nets</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Protective safety nets for children's safety in homes and playgrounds.</p>
              </div>
            </Link>

            {/* 7. Drying Cloth Hangers */}
            <Link to="/pull-dry-cloth-hangers" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
                <img 
                  src="/images/pull-dry-cloth-hangers-balconies1.jpg"
                  alt="Drying Cloth Hangers"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Drying Cloth Hangers</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Convenient retractable cloth hangers for drying clothes.</p>
              </div>
            </Link>

            {/* 8. Shade Net */}
            <Link to="/shade-net" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="h-64 bg-gray-50 relative overflow-hidden">
                <img 
                  src="/images/shade-nets-agricultural.webp"
                  alt="Shade Net"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto rounded bg-white/80 p-1 shadow"
              />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-lg">Shade Net</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Protective shade nets for agricultural and commercial applications.</p>
              </div>
            </Link>

          {/* 9. Balcony Safety Nets */}
          <Link to="/balcony-safety-nets" className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className="h-64 bg-gray-50 relative overflow-hidden">
              <img 
                src="/images/balcony-safety-nets-custom.jpg"
                alt="Balcony Safety Nets"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <img
                src="/images/jaranets-logo.png"
                alt="JaraNets Logo"
                className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
              />
            </div>
            <div className="p-6">
              <h4 className="font-semibold text-gray-900 mb-3 text-lg">Balcony Safety Nets</h4>
              <p className="text-gray-600 text-sm leading-relaxed">Durable balcony safety nets to protect children, pets, and belongings.</p>
            </div>
          </Link>

          </div>

          {/* View All Services Button */}
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Safety Nets?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive safety solutions with unmatched quality and service - serving Andhra Pradesh and Telangana with professional safety net installation, maintenance, repair, and consultation services
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center text-white"
              >
                <div className="flex justify-center mb-2">{stat.icon}</div>
                <div className="text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get Protected?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Serving all areas of Hyderabad including Secunderabad, Kukatpally, Gachibowli, HITEC City, Banjara Hills, Jubilee Hills, Begumpet, Ameerpet, Somajiguda, Abids, Koti, Nampally, Malakpet, Dilsukhnagar, L.B. Nagar, Uppal, Kothapet, Hayathnagar, Vanasthalipuram, Nagole, Boduppal, Ghatkesar, Medchal, Shamshabad, Rajendranagar, Serilingampally, Qutubullapur, Malkajgiri, Alwal, Sainikpuri, Kapra, Uppal, Nacharam, ECIL, Tarnaka, Habsiguda, Moula Ali, Bowenpally, Trimulgherry, Karkhana, Bolarum, BHEL, Patancheru, Jeedimetla, Balanagar, Kukatpally, Miyapur, Bachupally, Nizampet, Bachupally, Kompally, Suchitra, Dundigal, Shamirpet, and all surrounding areas. Join the growing community that has already secured their safety. Contact us today for a complimentary safety consultation on construction safety nets, sports protection systems, and customized safety solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+918074616049"
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Call Now: +91 8074 616 049
            </a>
            <a
              href="tel:+918466055207"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Call Now: +91 8466 055 207
            </a>
            <button
              onClick={() => openWhatsAppQuote()}
              className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer"
            >
              Request Quote
            </button>
            <Link
              to="/landing"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors border-2 border-green-600"
            >
              <Gift className="inline mr-2 h-6 w-6" />
              Get Safety Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
